import {
  buildBackgroundFetchConfig,
  shouldDrainOnLeavingForeground,
} from './offlineQueue'

export interface OfflineQueueBackgroundDeps {
  getPlatform: () => string
  drainOnFetch: boolean
  fetchConfig: ReturnType<typeof buildBackgroundFetchConfig>
  configureFetch: (
    config: ReturnType<typeof buildBackgroundFetchConfig>,
    onEvent: (taskId: string) => Promise<void>,
    onTimeout: (taskId: string) => Promise<void>,
  ) => Promise<unknown>
  finishFetch: (taskId: string) => Promise<void> | void
  addAppStateListener: (
    handler: (state: { isActive: boolean }) => void | Promise<void>,
  ) => Promise<void>
  runWhileBackgrounded: (work: () => Promise<void>) => Promise<void>
  drain: () => Promise<void>
  drainNative?: () => Promise<void>
}

export function shouldDrainOnFetch (
  platform: string,
  nativePluginAvailable: boolean,
): boolean {
  return platform !== 'android' || !nativePluginAvailable
}

export async function registerOfflineQueueBackground (
  deps: OfflineQueueBackgroundDeps,
): Promise<void> {
  if (deps.getPlatform() === 'web') {
    return
  }

  await deps.configureFetch(
    deps.fetchConfig,
    async (taskId) => {
      try {
        if (deps.drainNative) {
          await deps.drainNative()
        }
        if (deps.drainOnFetch) {
          await deps.drain()
        }
      } finally {
        await deps.finishFetch(taskId)
      }
    },
    async (taskId) => {
      await deps.finishFetch(taskId)
    },
  )

  await deps.addAppStateListener(async ({ isActive }) => {
    if (!shouldDrainOnLeavingForeground(isActive, deps.getPlatform())) {
      return
    }
    await deps.runWhileBackgrounded(deps.drain)
  })
}
